import { CalculationClassificationEnum } from "gen/model/calculationClassificationEnum";
import { CalculationTypeEnum } from "gen/model/calculationTypeEnum";
import { TransactionCalculationPayloadCreateDataRequest } from "gen/model/transactionCalculationPayloadCreateDataRequest";
import i18n from "i18next";
import { parse as mathParse } from "mathjs";
import { getErrorMessage } from "shared/utils/ErrorUtils";
import { z, ZodIssueCode } from "zod";

export const calculationFormSchema = z
  .object({
    name: z.string().min(1),
    classification: z.nativeEnum(CalculationClassificationEnum),
    calculation_type: z.nativeEnum(CalculationTypeEnum),
  })
  .passthrough()
  .superRefine((arg, ctx) => {
    const data =
      arg as unknown as TransactionCalculationPayloadCreateDataRequest;
    data.calculation_definitions.forEach((def, defIndex) => {
      if (
        arg.classification === "fee_basis" &&
        arg.calculation_type === "basic"
      ) {
        if (!def.fee_basis_categories?.length) {
          ctx.addIssue({
            code: ZodIssueCode.custom,
            message: i18n.t("errors:required"),
            path: ["calculation_definitions", defIndex, "fee_basis_categories"],
          });
        } else {
          def.fee_basis_categories.forEach((category, categoryIndex) => {
            if (!category.category_name) {
              ctx.addIssue({
                code: ZodIssueCode.custom,
                message: i18n.t("errors:required"),
                path: [
                  "calculation_definitions",
                  defIndex,
                  "fee_basis_categories",
                  categoryIndex,
                  "category_name",
                ],
              });
            }
          });
        }
        return;
      }

      if (data.calculation_definitions.length > 1) {
        const isFirst = defIndex === 0;
        const isLast = defIndex === data.calculation_definitions.length - 1;
        if (!isFirst && !def.start_date) {
          ctx.addIssue({
            code: ZodIssueCode.custom,
            message: i18n.t("errors:required"),
            path: ["calculation_definitions", defIndex, "start_date"],
          });
        }
        if (!isLast && !def.end_date) {
          ctx.addIssue({
            code: ZodIssueCode.custom,
            message: i18n.t("errors:required"),
            path: ["calculation_definitions", defIndex, "end_date"],
          });
        }
      }

      if (arg.calculation_type === "basic") {
        if (!def.transaction_codes.length) {
          ctx.addIssue({
            code: ZodIssueCode.custom,
            message: i18n.t("errors:required"),
            path: ["calculation_definitions", defIndex, "transaction_codes"],
          });
        } else {
          def.transaction_codes.forEach((code, codeIndex) => {
            if (!code.transaction_code_id) {
              ctx.addIssue({
                code: ZodIssueCode.custom,
                message: i18n.t("errors:required"),
                path: [
                  "calculation_definitions",
                  defIndex,
                  "transaction_codes",
                  codeIndex,
                  "transaction_code_id",
                ],
              });
            }
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (!code.add_or_subtract) {
              ctx.addIssue({
                code: ZodIssueCode.custom,
                message: i18n.t("errors:required"),
                path: [
                  "calculation_definitions",
                  defIndex,
                  "transaction_codes",
                  codeIndex,
                  "add_or_subtract",
                ],
              });
            }
          });
        }
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      } else if (arg.calculation_type === "advanced") {
        if (!def.eval_string) {
          ctx.addIssue({
            code: ZodIssueCode.custom,
            message: i18n.t("errors:required"),
            path: ["calculation_definitions", defIndex, "eval_string"],
          });
        } else {
          try {
            mathParse(def.eval_string);
          } catch (e) {
            ctx.addIssue({
              code: ZodIssueCode.custom,
              message: getErrorMessage(i18n.t, e),
              path: ["calculation_definitions", defIndex, "eval_string"],
            });
          }
        }
      }
    });
  });
